import type Konva from 'konva';
import { useEffect, useRef } from 'react';
import { Group, Line } from 'react-konva';
import { useShallow } from 'zustand/react/shallow';
import { clamp } from '~/lib/utils';
import { useStore } from '~/store';
import { setStageCursor } from './cursors';
import type { GuidelineAxis } from './slice';

const GUIDE_COLOR = 'rgba(108, 91, 158, 0.65)';
const GUIDE_SEL_COLOR = '#FFB300';
const HANDLE_HIT = 14;

type GuideProps = {
  axis: GuidelineAxis;
  index: number;
  coord: number;
  imageW: number;
  imageH: number;
};

function Guide({ axis, index, coord, imageW, imageH }: GuideProps) {
  const isVertical = axis === 'v';
  const sel = useStore((s) => s.editor.selectedEdge);
  const drawMode = useStore((s) => s.editor.drawMode);

  const isSelected = sel?.kind === 'guideline' && sel.axis === axis && sel.index === index;
  const interactive = drawMode === 'none';
  const points = isVertical ? [0, 0, 0, imageH] : [0, 0, imageW, 0];
  const cursor = isVertical ? 'col-resize' : 'row-resize';
  const select = (e: Konva.KonvaEventObject<Event>) => {
    e.cancelBubble = true;
    useStore.getState().setSelection({ kind: 'guideline', axis, index });
  };

  return (
    <Line
      x={isVertical ? coord : 0}
      y={isVertical ? 0 : coord}
      points={points}
      stroke={isSelected ? GUIDE_SEL_COLOR : GUIDE_COLOR}
      strokeWidth={isSelected ? 4 : 2}
      strokeScaleEnabled={false}
      hitStrokeWidth={HANDLE_HIT}
      dash={[6, 4]}
      draggable={interactive}
      listening={interactive}
      onMouseEnter={(e) => setStageCursor(e, cursor)}
      onMouseLeave={(e) => setStageCursor(e, '')}
      onClick={select}
      onTap={select}
      onDragStart={() => useStore.temporal.getState().pause()}
      onDragMove={(e) => {
        const node = e.target;
        const limit = isVertical ? imageW : imageH;
        const raw = isVertical ? node.x() : node.y();
        const next = clamp(0, limit, raw);
        if (isVertical) {
          node.y(0);
          if (raw !== next) node.x(next);
        } else {
          node.x(0);
          if (raw !== next) node.y(next);
        }
        useStore.getState().moveGuideline(axis, index, next);
      }}
      onDragEnd={() => useStore.temporal.getState().resume()}
    />
  );
}

/**
 * User-placed alignment guides. Renders Konva nodes inside the EditorStage
 * single Layer (matches `GridOverlay` pattern). Also wires Alt-click on the
 * stage to drop a new guideline at the click point — Shift held → horizontal,
 * else vertical.
 */
export function GuidelinesOverlay() {
  const dims = useStore((s) => s.source.dimensions);
  const verticals = useStore(useShallow((s) => s.editor.guidelines.vertical));
  const horizontals = useStore(useShallow((s) => s.editor.guidelines.horizontal));
  const groupRef = useRef<Konva.Group>(null);
  const hasDims = dims !== null;

  // Re-runs when `dims` flips from null to non-null so the listener attaches
  // once the Stage actually exists.
  useEffect(() => {
    if (!hasDims) return;
    const stage = groupRef.current?.getStage();
    if (!stage) return;
    const onMouseDown = (e: Konva.KonvaEventObject<MouseEvent>) => {
      if (!e.evt.altKey) return;
      const { editor, source, addGuideline } = useStore.getState();
      const d = source.dimensions;
      if (!d || editor.drawMode !== 'none') return;
      const pos = stage.getRelativePointerPosition();
      if (!pos) return;
      if (pos.x < 0 || pos.y < 0 || pos.x > d.w || pos.y > d.h) return;
      e.evt.preventDefault();
      e.cancelBubble = true;
      if (e.evt.shiftKey) addGuideline('h', pos.y);
      else addGuideline('v', pos.x);
    };
    stage.on('mousedown.guidelines', onMouseDown);
    return () => {
      stage.off('mousedown.guidelines', onMouseDown);
    };
  }, [hasDims]);

  if (!dims) return null;

  return (
    <Group ref={groupRef}>
      {verticals.map((coord, i) => (
        <Guide
          // biome-ignore lint/suspicious/noArrayIndexKey: structural position, not reorder-sensitive
          key={`v-${i}`}
          axis="v"
          index={i}
          coord={coord}
          imageW={dims.w}
          imageH={dims.h}
        />
      ))}
      {horizontals.map((coord, i) => (
        <Guide
          // biome-ignore lint/suspicious/noArrayIndexKey: structural position, not reorder-sensitive
          key={`h-${i}`}
          axis="h"
          index={i}
          coord={coord}
          imageW={dims.w}
          imageH={dims.h}
        />
      ))}
    </Group>
  );
}
